import { getCacheWithoutExpiry, getGeoInfo } from "../";
import client from "../../services/apiClient";
import { SLACK_CHANNELS } from "./constants";

const isPromiseLike = (value: unknown): value is Promise<unknown> =>
  value && typeof (value as any).then === "function";

const toSafeString = async (value: unknown): Promise<string> => {
  if (isPromiseLike(value)) {
    try {
      const resolvedValue = await value;
      return toSafeString(resolvedValue);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      return `Promise rejected: ${msg}`;
    }
  }

  if (value === null || value === undefined) {
    return "N/A";
  }

  if (typeof value === "string") {
    return value;
  }

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }

  if (value instanceof Error) {
    return value.stack || value.message || value.toString();
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const escapeGraphQLString = (value: unknown): string =>
  String(value)
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\r?\n/g, "\\n");

export const sendSlackMessage = async (params: any): Promise<void> => {
  const { VITE_API_URL_GRAPHQL, VITE_NODE_ENV } = import.meta.env;
  let fieldsString = "";
  const user = await getCacheWithoutExpiry("me");
  const userId = user && (user as any)?.id ? (user as any)?.id : "N/A";
  const userEmail = user && (user as any)?.email ? (user as any)?.email : "N/A";
  const userName = user && (user as any)?.name ? (user as any)?.name : "N/A";
  const teamName =
    user && (user as any)?.currentTeam ? (user as any)?.currentTeam : "NA";
  if (!Array.isArray(params?.fields)) {
    params.fields = [];
  }

  const defaultFields = [
    {
      title: "User ID",
      value: userId,
    },
    {
      title: "User Name",
      value: userName,
    },
    {
      title: "User Email",
      value: userEmail,
    },
    {
      title: "User Team",
      value: teamName,
    },
  ];
  Array.prototype.push.apply(params.fields, defaultFields);

  const defaultChannel =
    params?.type === "info"
      ? SLACK_CHANNELS.FRONTEND_INFO
      : SLACK_CHANNELS.FRONTEND_ERRORS;

  const defaultParam = {
    channel: defaultChannel,
    type: "error",
    heading: window.location.href,
    detail: "",
    fields: defaultFields,
  };

  const options = {
    ...defaultParam,
    ...params,
  };

  if (options.channel === "error") {
    options.channel = SLACK_CHANNELS.FRONTEND_ERRORS;
  } else if (options.channel === "info") {
    options.channel = SLACK_CHANNELS.FRONTEND_INFO;
  }

  getGeoInfo().then(async (geoData) => {
    let fields = [
      {
        title: "Environment",
        value: VITE_NODE_ENV,
      },
    ];

    if (geoData) {
      fields = [
        {
          title: "Environment",
          value: VITE_NODE_ENV,
        },
        {
          title: "IP Address",
          value: geoData.ip ? geoData.ip : "N/A",
        },
        {
          title: "Country Name",
          value: geoData.country_name ? geoData.country_name : "N/A",
        },
        {
          title: "City Name",
          value: geoData.city ? geoData.city : "N/A",
        },
      ];
    }

    const fieldsMerged = [...options.fields, ...fields];

    const normalizedFields = await Promise.all(
      fieldsMerged.map(async (obj): Promise<Record<string, string>> => {
        const normalizedObject: Record<string, string> = {};
        const entries = Object.entries(obj || {});
        for (const [key, value] of entries) {
          normalizedObject[key] = await toSafeString(value);
        }
        return normalizedObject;
      }),
    );

    options.fields = normalizedFields.filter(
      (v: Record<string, string>, i: number, a: Record<string, string>[]) =>
        a.findIndex((v2: Record<string, string>) =>
          ["title", "value"].every((k: string) => v2[k] === v[k]),
        ) === i,
    );

    const normalizedChannel = escapeGraphQLString(
      await toSafeString(options.channel),
    );
    const normalizedType = escapeGraphQLString(
      await toSafeString(options.type),
    );
    const normalizedHeading = escapeGraphQLString(
      await toSafeString(options.heading),
    );
    const normalizedDetail = escapeGraphQLString(
      await toSafeString(options.detail),
    );

    if (options.fields.length > 0) {
      fieldsString = "fields: [\n";
      options.fields.forEach((obj: Record<string, string>) => {
        fieldsString += "{\n";
        Object.keys(obj).forEach((obj2: string) => {
          fieldsString += obj2 + ': "' + escapeGraphQLString(obj[obj2]) + '"\n';
        });
        fieldsString += "},\n";
      });
      fieldsString += "]";
    }

    const data = JSON.stringify({
      query: `mutation{
        sendSlackMessage(slackData: {
          channel: "${normalizedChannel}"
          type: "${normalizedType}"
          heading: "${normalizedHeading}"
          detail: "${normalizedDetail}"
            ${fieldsString}
        })
        {
            success
            message
        }
    }`,
      variables: {},
    });

    const Options = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    client
      .post(VITE_API_URL_GRAPHQL, data, Options)
      // .then(function (response) {
      .then(function () {
        // ;
      })
      // .catch(function (error) {
      .catch(function () {
        //
      });

    // axios(config)
  });
};
